import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Modal, Alert, Spinner, Row, Col, InputGroup, Form, Badge } from "react-bootstrap";
import { FaTrashAlt, FaEye, FaEdit, FaSearch, FaTimesCircle, FaEuroSign, FaDownload } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import { useLocation, useNavigate } from "react-router-dom";
import { adminInvoicesAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

function InvoiceTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelId, setCancelId] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentInvoiceUrl, setCurrentInvoiceUrl] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null); // ✅ إضافة هذا
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedId, setHighlightedId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [formData, setFormData] = useState({
    firma_name: '',
    kunde_name: '',
    strasse_hausnummer: '',
    postleitzahl_staat_ort: '',
    land: '',
    email: '',
    ust_idnr: '',
  });

  const location = useLocation();
  const isFirstSearchRender = useRef(true);

  // Fetch invoices data with search
  const fetchInvoices = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      setError("");
      const response = await adminInvoicesAPI.index({
        page,
        per_page: pagination.per_page,
        search,
      });

      if (response.success) {
        setData(response.data.data || []);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t("failed_to_fetch_data"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_fetch_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("failed_to_fetch_data"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_fetch_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }
    
    if (type === "file") {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData((prev) => ({
            ...prev,
            [name]: reader.result,
          }));
        };
        reader.readAsDataURL(file);
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
      return;
    }
    
    if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? "" : Number(value),
      }));
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openAddModal = () => {
    setModalType('add');
    setFormData({
      firma_name: '',
      kunde_name: '',
      strasse_hausnummer: '',
      postleitzahl_staat_ort: '',
      land: '',
      email: '',
      ust_idnr: '',
    });
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setModalType('edit');
    setEditingId(admin.id);
    setFormData({
      firma_name: admin.firma_name || '',
      kunde_name: admin.kunde_name || '',
      strasse_hausnummer: admin.strasse_hausnummer || '',
      postleitzahl_staat_ort: admin.postleitzahl_staat_ort || '',
      land: admin.land || '',
      email: admin.email || '',
      ust_idnr: admin.ust_idnr || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (modalType === 'add') {
        response = await adminInvoicesAPI.storeCompanyInfo(formDataToSend);
      } else {
        response = await adminInvoicesAPI.update(editingId, formDataToSend);
      }

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: modalType === 'add' ? t("admin_added_successfully") : t("admin_updated_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowModal(false);
        fetchInvoices(pagination.current_page);
      } else {
        setError(response.message || t("operation_failed"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("operation_failed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("operation_failed"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("operation_failed"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error submitting form:", err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  // useEffect(() => {
  //   if (isFirstSearchRender.current) {
  //     isFirstSearchRender.current = false;
  //     return;
  //   }

  //   const delayDebounceFn = setTimeout(() => {
  //     fetchInvoices(1, searchTerm);
  //   }, 1500);

  //   return () => clearTimeout(delayDebounceFn);
  // }, [searchTerm]);

  useEffect(() => {
    if (location.state?.fromPage || location.state?.highlightId) {
      const page = location.state.fromPage || 1;
      const highlightId = location.state.highlightId || null;

      setHighlightedId(highlightId ? Number(highlightId) : null);
      fetchInvoices(page);

      // امسح الـ state من الـ history
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchInvoices();
    }
  }, []);

  // ✅ View invoice PDF
  const viewInvoice = (invoice) => {
    if (invoice.invoice_url) {
      setSelectedInvoice(invoice);
      setCurrentInvoiceUrl(invoice.invoice_url);
      setShowInvoiceModal(true);
    } else {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("invoice_url_not_available"),
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  // ✅ Download invoice PDF
  const handleDownload = async (invoice) => {
    if (!invoice?.invoice_url) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('no_invoice_available'),
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    setDownloading(true);
    setDownloadingId(invoice.id);

    try {
      // ✅ فتح الرابط في نافذة جديدة للتحميل
      const link = document.createElement("a");
      link.href = invoice.invoice_url;
      link.target = "_blank";
      link.download = `invoice-${invoice.id || 'download'}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error("Download error:", err);
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('download_failed'),
        timer: 2000,
        showConfirmButton: false
      });
    } finally {
      setDownloading(false);
      setDownloadingId(null);
    }
  };

  const editInvoice = async (id) => {
    try {
      setLoading(true);
      const response = await adminInvoicesAPI.show(id);
      if (response.success) {
        navigate(`/admin/invoices/edit/${id}`, {
          state: {
            invoiceData: response.data,
            fromPage: pagination.current_page,   // ← الصفحة الحالية
            highlightId: id,                     // ← الصف اللي هيتظلل
          },
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_fetch_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_fetch_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error fetching invoice for edit:", err);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await adminInvoicesAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("invoice_deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowDelete(false);
        fetchInvoices(pagination.current_page);
      } else {
        setError(response.message || t("delete_failed"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("delete_failed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("delete_failed"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("delete_failed"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error deleting invoice:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleMarkPaid = async (id) => {
      Swal.fire({
        title: t("confirm_action"),
        text: t("confirm_mark_paid"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: t("yes_mark_paid"),
        cancelButtonText: t("cancel"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setLoading(true);
            const response = await adminInvoicesAPI.markPaid(id);
            
            if (response.success) {
              Swal.fire({
                icon: "success",
                title: t("success"),
                text: t("booking.marked_paid_successfully"),
                timer: 3000,
                showConfirmButton: false,
              });
              fetchInvoices();
            } else {
              throw new Error(response.message);
            }
          } catch (err) {
            Swal.fire({
              icon: "error",
              title: t("error"),
              text: err.message || t("failed_to_mark_paid"),
              timer: 3000,
            });
          } finally {
            setLoading(false);
          }
        }
      });
    };

  const handleCancelInvoiceClick = (id) => {
    setCancelId(id);
    setShowCancelModal(true);
  };

  const handleCancelInvoiceConfirm = async () => {
    try {
      setLoading(true);
      const response = await adminInvoicesAPI.cancel(cancelId);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("invoice_canceled_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        const updatedData = data.map(item =>
          item.id === cancelId ? { ...item, status: 'canceled', invoice_url: response.data?.invoice_url } : item
        );
        setData(updatedData);
        fetchInvoices(pagination.current_page);
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_cancel_invoice"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Error canceling invoice:", err);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_cancel_invoice"),
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
      setShowCancelModal(false);
      setCancelId(null);
    }
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="fw-bold mb-0">🧾 {t('Invoices List')}</h3>

        <div className="d-flex flex-wrap gap-3">
          <Button className='btn btn-success' onClick={openAddModal} disabled={loading}>
            ➕ {t('add_company_data')}
          </Button>

          <Button 
            className="add-btn btn btn-primary" 
            onClick={() => navigate("/admin/invoices/new")}
            disabled={loading}
          >
            ➕ {t('Neu_hinzufügen')}
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('search_title')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={clearSearch}
                title={t("reset_search")}
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : data.length === 0 ? (
        <Alert variant="info">{t('no_data_available')}</Alert>
      ) : (
        <>
          <div className="table-container shadow-sm rounded">
            <div className="table-responsive">
              <Table bordered hover className="custom-table mb-0 text-center">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>{t('Title')}</th>
                    <th>{t('zahlungsziel')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry, idx) => (
                    <tr key={entry.id} className={Number(highlightedId) === Number(entry.id) ? "row-highlighted" : ""}>
                      <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                      <td>
                        {searchTerm ? (
                          <span 
                            dangerouslySetInnerHTML={{
                              __html: entry.title?.replace(
                                new RegExp(searchTerm, 'gi'), 
                                match => `<mark class="bg-warning">${match}</mark>`
                              )
                            }} 
                          />
                        ) : (
                          entry.title
                        )}
                        {entry.status === 'canceled' && (
                          <Badge bg="danger" className="ms-2">{t('canceled')}</Badge>
                        )}
                      </td>
                      <td>{entry.zahlungsziel}</td>
                      <td className="d-flex justify-content-center gap-2 flex-wrap">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-2"
                          onClick={() => viewInvoice(entry)}
                          title={t('View Invoice')}
                          disabled={loading}
                        >
                          <FaEye />
                        </Button>
                        <Button
                          className="add-btn me-2"
                          size="sm"
                          onClick={() => editInvoice(entry.id)}
                          title={t('Edit')}
                          disabled={loading || entry.status === 'canceled'}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          title={t('mark_paid')}
                          onClick={() => handleMarkPaid(entry.id)}
                          disabled={loading || entry.payment_status}
                        >
                          <FaEuroSign /> {t('mark_paid')}
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-2"
                          onClick={() => handleCancelInvoiceClick(entry.id)}
                          title={t('cancel_invoice')}
                          disabled={loading || entry.status === 'canceled'}
                        >
                          <FaTimesCircle /> {t('cancel_invoice')}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteId(entry.id);
                            setShowDelete(true);
                          }}
                          title={t('Delete')}
                          disabled={loading}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          {pagination.total > 0 && (
            <PaginationComponent
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onPageChange={fetchInvoices}
              disabled={loading}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('confirm_delete_invoice')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('Deleting...') : t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Invoice Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('cancel_invoice')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirm_cancel_invoice')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            {t('cancel')}
          </Button>
          <Button variant="warning" onClick={handleCancelInvoiceConfirm} disabled={loading}>
            {loading ? t('processing') : t('confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ✅ Invoice View Modal with Download */}
      <Modal 
        show={showInvoiceModal} 
        onHide={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
          setCurrentInvoiceUrl('');
        }} 
        size="xl" 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEye className="me-2" />
            {t('viewInvoice')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '80vh' }}>
          {currentInvoiceUrl ? (
            <iframe
              src={currentInvoiceUrl}
              title="Invoice PDF"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Alert variant="warning" className="m-3">
                {t('no_invoice_available')}
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowInvoiceModal(false);
              setSelectedInvoice(null);
              setCurrentInvoiceUrl('');
            }}
          >
            {t("close")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (selectedInvoice) {
                handleDownload(selectedInvoice);
              }
            }}
            disabled={!selectedInvoice?.invoice_url || downloading}
          >
            {downloading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("downloading")}
              </>
            ) : (
              <>
                <FaDownload className="me-2" />
                {t("booking.download_pdf")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'add' ? t('add_company_information') : t('edit_company_information')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('firma_name')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firma_name"
                    value={formData.firma_name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('kunde_name')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="kunde_name"
                    value={formData.kunde_name}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('strasse_hausnummer')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="strasse_hausnummer"
                    value={formData.strasse_hausnummer}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('postleitzahl_staat_ort')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="postleitzahl_staat_ort"
                    value={formData.postleitzahl_staat_ort}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('land')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="land"
                    value={formData.land}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('Email')} *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('ust_idnr')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="ust_idnr"
                    value={formData.ust_idnr}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
            </div>

          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('Saving...') : t('save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default InvoiceTable;